import { Product } from "~/types/product";
import {
  Heading,
  Definition,
  Descr,
  Term,
  Table,
} from "~/components/ProductDescription/components";

type Props = {
  product: Product;
};

const ProductDescription: React.FC<Props> = ({ product }) => {
  const {
    features,
    fullName,
    styleCodeAliases,
    careInstructions,
    description,
    disclaimers,
    sizingChart,
    fabricWeight,
    availableSizes,
  } = product.information ?? {};

  return (
    <div className="flex flex-col gap-6 p-8 leading-[1.5] text-black">
      <h4 className="description__heading font-serif text-[1.375rem] font-bold text-blue900">
        Description
      </h4>
      <div className="description__specs">
        <dl>
          <Definition>
            <Term>Name</Term>
            <Descr>{fullName}</Descr>
          </Definition>
          <Definition>
            <Term>Style</Term>
            <Descr>{product.styleCode}</Descr>
          </Definition>
          {fabricWeight?.length ? (
            <Definition>
              <Term>Fabric Weight</Term>
              <Descr>{fabricWeight}</Descr>
            </Definition>
          ) : null}
          {availableSizes?.length ? (
            <Definition>
              <Term>Sizes</Term>
              <Descr>{availableSizes?.join(", ")}</Descr>
            </Definition>
          ) : null}
          {styleCodeAliases?.length ? (
            <Definition>
              <Term>Aliases</Term>
              <Descr>{styleCodeAliases}</Descr>
            </Definition>
          ) : null}
        </dl>
      </div>
      {features ? (
        <div className="description__features">
          <Heading>Features</Heading>
          <ul className="list-disc pl-4 text-[0.875rem]">
            {features.map((feature, index) => (
              <li
                key={index}
                className="mb-1"
              >
                {feature}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {sizingChart?.length ? (
        <div className="description__sizing-chart">
          <Heading>Sizing Chart</Heading>
          <Table data={sizingChart} />
        </div>
      ) : null}
      {careInstructions?.length ? (
        <div className="description__care-instructions">
          <Heading>Care Instructions</Heading>
          <ul className="list-disc columns-2 pl-4 text-[0.875rem]">
            {careInstructions?.map((feature, index) => (
              <li
                key={index}
                className="mb-1"
              >
                {feature}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {description ? (
        <div className="description__description">
          <Heading>Product Information</Heading>
          <p
            className="text-[0.875rem]"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      ) : null}
      {disclaimers?.length ? (
        <div className="description__disclaimers">
          <Heading>Notes & Disclaimers</Heading>
          <ol className="list-decimal pl-4 text-[0.875rem]">
            {disclaimers?.map((feature, index) => (
              <li
                key={index}
                className="mb-1"
              >
                {feature}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
};

export default ProductDescription;
